import { ArrowRightOutlined } from "@ant-design/icons";
import { SSO_OIDC_PROVIDERS } from "@p0security/shared/configuration/constant";
import {
  INVALID_EMAIL_DOMAIN_ERROR,
  isValidEmailDomain,
} from "@p0security/shared/configuration/utils";
import * as Sentry from "@sentry/react";
import { Image, Row, Typography, message } from "antd";
import {
  Auth,
  AuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithPopup,
} from "firebase/auth";
import { useFlags } from "launchdarkly-react-client-sdk";
import { usePostHog } from "posthog-js/react";
import * as React from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateAccountPageStore } from "../../../store/createAccountStore";
import { getEnvironment } from "../../../utils/environment";
import CompanySection from "./CompanySection";
import OrgInfoForm from "./OrgInfoForm";
import SignUpForm from "./SignUpForm";
import {
  LoginSectionCol,
  StyledButtonSpacing,
  StyledContainer,
  StyledLoginLink,
  StyledLogos,
  StyledMainContent,
  StyledPrivacy,
} from "./styles";

const { Title } = Typography;

const saveUserEmail = async (email: string, userDisplayName: string) => {
  if (!email || !userDisplayName) {
    return;
  }
  const fetchUrl = `${getEnvironment().apiUrl}/provision/save-user-email`;
  const responsePromise = fetch(fetchUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      name: userDisplayName,
    }),
  });

  try {
    await responsePromise;
  } catch {
    throw new Error("Network error when signing up. Please try again later.");
  }

  const response = await responsePromise;
  if (!response.ok) {
    const responseData = await response.json();
    throw new Error(
      responseData?.errorMessage || "Failed to sign up. Please try again later."
    );
  }
};

const CreateAccountPage: React.FC = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const flags = useFlags();

  const {
    setIsSubmittingInfo,
    setIsGoogleSsoLoading,
    setIsEntraSsoLoading,
    setNewAccountInfo,
    newAccountInfo,
    showOrgInfo,
    setShowOrgInfo,
  } = useCreateAccountPageStore();
  const [messageApi, contextHolder] = message.useMessage();

  const createAccount = useCallback(
    async (orgSlug: string) => {
      const { email, userDisplayName, ssoProvider } = newAccountInfo;
      setIsSubmittingInfo(true);

      try {
        if (flags.allowWorkEmail && !isValidEmailDomain(email)) {
          throw new Error(INVALID_EMAIL_DOMAIN_ERROR);
        }

        const fetchUrl = `${getEnvironment().apiUrl}/provision/create-tenant`;
        const responsePromise = fetch(fetchUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            orgSlug,
            name: userDisplayName,
            ssoProvider,
          }),
        });

        // This only handles the case of network errors, not errors returned by the server
        try {
          await responsePromise;
        } catch (error) {
          throw new Error(
            "Network error when creating account. Please try again later."
          );
        }

        const response = await responsePromise;
        const responseData = await response.json();
        if (!response.ok) {
          throw new Error(
            responseData?.errorMessage || "Failed to create account"
          );
        }

        posthog?.capture("new_account_created", {
          email: email,
          org: orgSlug,
        });

        navigate(`/o/${responseData.orgSlug}/welcome?tenant=new`);
      } catch (error) {
        messageApi.error(`${error instanceof Error ? error.message : error}`);
      } finally {
        setIsSubmittingInfo(false);
        setIsGoogleSsoLoading(false);
      }
    },
    [
      newAccountInfo,
      setIsSubmittingInfo,
      flags.allowWorkEmail,
      messageApi,
      posthog,
      navigate,
      setIsGoogleSsoLoading,
    ]
  );

  const handleSsoSignUp = useCallback(
    async ({
      key,
      label,
      oauth,
      ssoProvider,
      setLoading,
    }: {
      key: string;
      label: string;
      oauth: AuthProvider;
      ssoProvider: SSO_OIDC_PROVIDERS;
      setLoading: (isLoading: boolean) => void;
    }) => {
      const auth: Auth = getAuth();
      setLoading(true);
      try {
        const response = await signInWithPopup(auth, oauth);
        const email = response.user.email;
        const displayName = response.user.displayName;
        if (!email || !displayName) {
          throw new Error(`Failed to retrieve email from ${label}`);
        }

        if (flags.allowWorkEmail && !isValidEmailDomain(email)) {
          throw new Error(INVALID_EMAIL_DOMAIN_ERROR);
        }

        posthog?.capture(`new_account_${key}_sso_signup`, {
          email: email,
        });

        await saveUserEmail(email, displayName);
        setNewAccountInfo({
          email,
          userDisplayName: displayName,
          ssoProvider: ssoProvider,
        });
        setShowOrgInfo(true);
      } catch (error: unknown) {
        Sentry.captureException(error);
        messageApi.error(
          error instanceof Error
            ? error.message
            : `Failed to sign up with ${label}. Please try again later.`
        );
      } finally {
        setLoading(false);
      }
    },
    [
      flags.allowWorkEmail,
      posthog,
      setNewAccountInfo,
      setShowOrgInfo,
      messageApi,
    ]
  );

  const handleGoogleSignUp = useCallback(async () => {
    handleSsoSignUp({
      key: "google",
      label: "Google",
      oauth: new GoogleAuthProvider(),
      ssoProvider: SSO_OIDC_PROVIDERS.google,
      setLoading: setIsGoogleSsoLoading,
    });
  }, [handleSsoSignUp, setIsGoogleSsoLoading]);

  const handleEntraSignUp = useCallback(async () => {
    handleSsoSignUp({
      key: "entra",
      label: "Entra",
      oauth: new OAuthProvider(`oidc.${SSO_OIDC_PROVIDERS.azure}`),
      ssoProvider: SSO_OIDC_PROVIDERS.azure,
      setLoading: setIsEntraSsoLoading,
    });
  }, [handleSsoSignUp, setIsEntraSsoLoading]);

  const onEmailSignUp = useCallback(
    async ({ email }: { email: string }) => {
      try {
        posthog?.capture("new_account_email_signup", {
          email: email,
        });
        const userDisplayName = email.split("@")[0].replace(".", " ");
        await saveUserEmail(email, userDisplayName);
        setNewAccountInfo({
          email,
          userDisplayName,
        });
        setShowOrgInfo(true);
      } catch (error: unknown) {
        messageApi.error(
          error instanceof Error
            ? error.message
            : "Failed to sign up with email. Please try again later."
        );
      }
    },
    [posthog, setNewAccountInfo, setShowOrgInfo, messageApi]
  );

  return (
    <StyledContainer>
      {contextHolder}
      <Row gutter={16} style={{ height: "100vh" }}>
        <CompanySection />
        <LoginSectionCol xs={24} md={12}>
          <StyledLoginLink>
            <Image
              preview={false}
              src={"/p0 dark logomark.png"}
              alt="P0"
              height={30}
              width={30}
            />
            <div className="login-text">
              Have an account?
              <a href={"./login"}>
                Log in <ArrowRightOutlined />
              </a>
            </div>
          </StyledLoginLink>
          <StyledMainContent>
            <Title level={4}>
              {showOrgInfo
                ? `What's the name of your company or team?`
                : "Control cloud entitlements for your engineers"}
            </Title>
            <StyledButtonSpacing direction="vertical">
              {showOrgInfo ? (
                <OrgInfoForm handleCreateAccount={createAccount} />
              ) : (
                <SignUpForm
                  handleGoogleSignUp={handleGoogleSignUp}
                  handleEntraSignUp={handleEntraSignUp}
                  onEmailSignUp={onEmailSignUp}
                />
              )}
            </StyledButtonSpacing>
            <StyledPrivacy>
              By signing up, you agree to our
              <a href="./tos">Terms of Service</a> and
              <a href="./privacy">Privacy Policy.</a>
            </StyledPrivacy>
          </StyledMainContent>
          <StyledLogos>
            <Image preview={false} src={"/SOC.png"} alt="P0" width={80} />
            <Image
              preview={false}
              src={"/gcp_partner.png"}
              alt="P0"
              width={80}
            />
          </StyledLogos>
        </LoginSectionCol>
      </Row>
    </StyledContainer>
  );
};

export default CreateAccountPage;
